/* import __COLOCATED_TEMPLATE__ from './bulk-schedule-agent-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import moment from 'moment-timezone';
import type CustomReportsService from 'embercom/services/custom-reports-service';

interface AgentOption {
  label: string;
  key: string;
}

interface Args {
  onClose: () => void;
  onSubmit: (
    agentTypes: string[],
    availabilityAction: string,
    scheduledAt: string,
    timezone: string,
    revertAt?: string,
  ) => void;
  isBulkActionRunning: boolean;
}

interface Signature {
  Args: Args;
}

export default class BulkScheduleAgentModal extends Component<Signature> {
  @service declare appService: AppService;
  @service declare customReportsService: CustomReportsService;
  @service declare intl: IntlService;

  @tracked _selectedTimezone: string | null = null;
  @tracked selectedAgentKeys: Set<string> = new Set(['chatbot', 'copilot']);
  @tracked selectedAction: 'enable' | 'disable' = 'enable';
  @tracked _scheduledDate: Date | null = null;
  @tracked showRevertAt = false;
  @tracked _revertAt: Date | null = null;

  get selectedTimezone(): string {
    return this._selectedTimezone ?? this.appService.app.timezone;
  }

  set selectedTimezone(value: string) {
    this._selectedTimezone = value;
  }

  get scheduledDate(): Date {
    return this._scheduledDate ?? this.defaultScheduledDate;
  }

  set scheduledDate(value: Date) {
    this._scheduledDate = value;
  }

  get revertAt(): Date {
    return this._revertAt ?? this.defaultRevertAt;
  }

  set revertAt(value: Date) {
    this._revertAt = value;
  }

  get canShowSalesAgentToggle(): boolean {
    return this.appService.app.canShowFinSalesRole;
  }

  get canShowEcommerceAgentToggle(): boolean {
    return this.appService.app.canShowFinEcommerceRole;
  }

  get serviceAgentLabel(): string {
    if (this.canShowSalesAgentToggle || this.canShowEcommerceAgentToggle) {
      return this.appService.app.canUseStandalone
        ? this.intl.t(
            'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-agent-for-service',
          )
        : this.intl.t(
            'knowledge-hub.content-editor.shared.visibility-section.fin-ai-agent-for-service',
          );
    }
    return this.appService.app.canUseStandalone
      ? this.intl.t(
          'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-agent',
        )
      : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.fin-ai-agent');
  }

  get agentOptions(): AgentOption[] {
    return [
      {
        label: this.serviceAgentLabel,
        key: 'chatbot',
      },
      {
        label: this.appService.app.canUseStandalone
          ? this.intl.t(
              'knowledge-hub.content-editor.shared.visibility-section.standalone.fin-ai-copilot',
            )
          : this.intl.t('knowledge-hub.content-editor.shared.visibility-section.fin-ai-copilot'),
        key: 'copilot',
      },
    ];
  }

  get today() {
    return new Date();
  }

  get agentSelectItems() {
    return this.agentOptions.map((opt) => ({
      value: opt.key,
      text: opt.label,
      isSelected: this.selectedAgentKeys.has(opt.key),
    }));
  }

  get selectedAgentsLabel() {
    let selected = this.agentOptions.filter((opt) => this.selectedAgentKeys.has(opt.key));
    if (selected.length === 0) {
      return this.intl.t(
        'knowledge-hub.filterable-list.bulk-actions.schedule-agent.select-ai-product',
      );
    }
    let labels = selected.map((opt) => opt.label);
    if (labels.length === 1) {
      return labels[0];
    }
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  }

  get defaultScheduledDate() {
    return moment
      .tz(this.selectedTimezone)
      .add(1, 'day')
      .hour(9)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
  }

  get defaultRevertAt() {
    return moment(this.scheduledDate).add(1, 'hour').second(0).millisecond(0).toDate();
  }

  get timezoneOptions(): { text: string; value: string }[] {
    return this.customReportsService.timezones().map((tz) => ({
      text: tz.description,
      value: tz.description,
    }));
  }

  get selectedTimezoneDescription(): string {
    let tz = this.customReportsService.timezones().find((t) => t.value === this.selectedTimezone);
    return tz?.description ?? this.selectedTimezone;
  }

  // Re-anchor scheduledDate and revertAt so each UTC instant means "same wall-clock,
  // new timezone". Without this, the Date values keep representing the old zone's
  // wall-clock and the future-date check fails when the user picks a westward
  // timezone (e.g. GMT+1 from GMT+6).
  @action onTimezoneChange(description: string) {
    let tz = this.customReportsService.timezones().find((t) => t.description === description);
    if (!tz) {
      return;
    }
    let oldTz = this.selectedTimezone;
    let startWallClock = moment(this.scheduledDate).tz(oldTz).format('YYYY-MM-DD HH:mm:ss');
    let revertWallClock = moment(this.revertAt).tz(oldTz).format('YYYY-MM-DD HH:mm:ss');
    this.selectedTimezone = tz.value;
    this.scheduledDate = moment.tz(startWallClock, 'YYYY-MM-DD HH:mm:ss', tz.value).toDate();
    this.revertAt = moment.tz(revertWallClock, 'YYYY-MM-DD HH:mm:ss', tz.value).toDate();
  }

  get scheduledDateError() {
    if (this.scheduledDate <= new Date()) {
      return this.intl.t(
        'knowledge-hub.filterable-list.bulk-actions.schedule-agent.start-date-error',
      );
    }
    return null;
  }

  get endDateTooltip() {
    return this.selectedAction === 'enable'
      ? this.intl.t(
          'knowledge-hub.filterable-list.bulk-actions.schedule-agent.end-date-tooltip-enable',
        )
      : this.intl.t(
          'knowledge-hub.filterable-list.bulk-actions.schedule-agent.end-date-tooltip-disable',
        );
  }

  get revertAtError() {
    if (this.showRevertAt && this.revertAt <= this.scheduledDate) {
      return this.intl.t(
        'knowledge-hub.filterable-list.bulk-actions.schedule-agent.end-date-error',
      );
    }
    return null;
  }

  get canSave() {
    return (
      this.selectedAgentKeys.size > 0 &&
      !this.args.isBulkActionRunning &&
      !this.scheduledDateError &&
      !this.revertAtError
    );
  }

  @action onAgentSelectionChange(selectedValues: string[]) {
    this.selectedAgentKeys = new Set(selectedValues);
  }

  @action onActionChange(value: 'enable' | 'disable') {
    this.selectedAction = value;
  }

  @action onScheduledDateChange(date: Date) {
    this.scheduledDate = date;
  }

  @action onRevertAtChange(date: Date) {
    this.revertAt = date;
  }

  @action toggleRevertAt() {
    this.showRevertAt = !this.showRevertAt;
  }

  @action submit() {
    if (!this.canSave) {
      return;
    }
    this.args.onSubmit(
      Array.from(this.selectedAgentKeys),
      this.selectedAction,
      this.scheduledDate.toISOString(),
      this.selectedTimezone,
      this.showRevertAt ? this.revertAt.toISOString() : undefined,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::BulkActions::BulkScheduleAgentModal': typeof BulkScheduleAgentModal;
    'knowledge-hub/filterable-list/bulk-actions/bulk-schedule-agent-modal': typeof BulkScheduleAgentModal;
  }
}
